/** On localhost, hit same-origin `/data` (Vite proxy) to avoid API CORS during preview. */
function resolveApiBase(): string {
  if (import.meta.env.VITE_API_BASE) return import.meta.env.VITE_API_BASE;
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return '';
  }
  return 'https://api.thisiszacharygray.com';
}

export const API_BASE = resolveApiBase();
export const MEDIA_BASE = import.meta.env.VITE_MEDIA_BASE || 'https://media.thisiszacharygray.com';

import type { MusicCatalog, PhotoPost, Release, SiteInfo, Track } from '@/types';
import type { RadioSchedule } from '@/lib/radioSchedule';

export type { MusicCatalog, PhotoPost, Release, SiteInfo, Track };
export type { RadioSchedule };
/** Catalog row / detail shell — omits heavy fields; keeps streamLinks for Listen. */
export type SlimRelease = Omit<Release, 'tracks' | 'description' | 'credits'>;

export function mediaUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return new URL(normalized, MEDIA_BASE).href;
}

export function artworkUrl(path: string): string {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const art = normalized.match(/\/img\/artwork\/([a-z0-9-]+)\./i);
  if (art) return mediaUrl(`/img/artwork/${art[1]}.jpg`);
  if (path.includes('artwork')) {
    const id = path.match(/artwork\/([a-z0-9-]+)/i)?.[1];
    if (id) return mediaUrl(`/img/artwork/${id}.jpg`);
  }
  return new URL(path.replace(/^\//, ''), window.location.origin).href;
}

/** MIME for Media Session artwork — wrong type makes macOS Control Center flicker. */
export function artworkMimeType(src: string): string {
  const path = src.split('?')[0]?.toLowerCase() ?? '';
  if (path.endsWith('.png')) return 'image/png';
  if (path.endsWith('.webp')) return 'image/webp';
  if (path.endsWith('.gif')) return 'image/gif';
  if (path.endsWith('.svg')) return 'image/svg+xml';
  return 'image/jpeg';
}

/**
 * Stable MediaMetadata artwork entries. Same absolute src at multiple sizes so
 * macOS/iOS pick a slot without re-fetching alternate URLs.
 */
export function mediaSessionArtwork(
  path: string,
): { src: string; sizes: string; type: string }[] {
  const src = artworkUrl(path);
  if (!src) return [];
  const type = artworkMimeType(src);
  return [
    { src, sizes: '96x96', type },
    { src, sizes: '256x256', type },
    { src, sizes: '512x512', type },
  ];
}

function endpointForType(type: string): string {
  const t = type.toLowerCase();
  if (t === 'compilation') return 'compilations';
  if (t === 'remix') return 'remixes';
  if (t === 'collaboration' || t === 'featured' || t === 'feature') {
    return 'collaborations';
  }
  return 'releases';
}

/** In-flight / resolved music catalog (shared across list, search, boot). */
let musicPromise: Promise<MusicCatalog> | null = null;
let musicCache: MusicCatalog | null = null;

export function getCachedMusicCatalog(): MusicCatalog | null {
  return musicCache;
}

/** Flatten + date-sort releases for the music catalog list. */
export function flattenMusicCatalog(data: MusicCatalog): SlimRelease[] {
  const all = [
    ...data.releases,
    ...data.compilations,
    ...data.remixes,
    ...data.collaborations,
  ] as SlimRelease[];
  all.sort(
    (a, b) =>
      new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime(),
  );
  return all;
}

export function getCachedMusicReleases(): SlimRelease[] | null {
  return musicCache ? flattenMusicCatalog(musicCache) : null;
}

export function preloadMusicCatalog(): Promise<MusicCatalog> {
  if (!musicPromise) {
    musicPromise = fetch(`${API_BASE}/data/music`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to fetch music');
        const data = (await res.json()) as MusicCatalog;
        musicCache = data;
        return data;
      })
      .catch((err) => {
        musicPromise = null;
        throw err;
      });
  }
  return musicPromise;
}

export async function fetchMusicCatalog(): Promise<MusicCatalog> {
  return preloadMusicCatalog();
}

/** Shared UTC radio schedule (playlist + epoch). Audio: media `/stream/radio.m3u8`. */
export async function fetchRadio(): Promise<RadioSchedule> {
  // cache:'no-store' only — do not send Cache-Control/Pragma request headers
  // (they trigger CORS preflight; API allowlist is Authorization + Content-Type).
  const res = await fetch(`${API_BASE}/data/music/radio`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Failed to fetch radio schedule');
  return res.json();
}

export async function fetchRelease(id: string, type: string): Promise<Release> {
  const endpoint = endpointForType(type);
  const res = await fetch(`${API_BASE}/data/music/${endpoint}/${id}`);
  if (!res.ok) throw new Error('Failed to fetch release');
  return res.json();
}

async function tryFetchReleaseEndpoint(
  endpoint: string,
  id: string,
): Promise<Release | null> {
  const res = await fetch(`${API_BASE}/data/music/${endpoint}/${id}`);
  if (res.ok) return res.json();
  if (res.status !== 404) {
    throw new Error(`Failed to fetch release (${res.status})`);
  }
  return null;
}

/** Resolve a release by id without requiring catalog type (deep-link / direct load). */
export async function fetchReleaseById(
  id: string,
  typeHint?: string,
): Promise<Release> {
  const endpoints = [
    'releases',
    'compilations',
    'remixes',
    'collaborations',
  ] as const;
  const preferred = typeHint ? endpointForType(typeHint) : null;

  if (preferred) {
    const hit = await tryFetchReleaseEndpoint(preferred, id);
    if (hit) return hit;
  }

  // No hint (or preferred 404): probe the rest in parallel — direct loads
  // used to wait on up to four serial 404s before Listen/tracks appeared.
  const rest = preferred
    ? endpoints.filter((e) => e !== preferred)
    : [...endpoints];
  const results = await Promise.all(
    rest.map((endpoint) => tryFetchReleaseEndpoint(endpoint, id)),
  );
  const found = results.find((r): r is Release => Boolean(r));
  if (found) return found;
  throw new Error('Failed to fetch release (404)');
}

/** Drop hidden posts/slides (API also filters; kept for stale/cached clients). */
export function filterPublicPhotoPost(post: PhotoPost | null | undefined): PhotoPost | null {
  if (!post || post.hidden === true) return null;
  const media = (post.media || []).filter((m) => m && m.hidden !== true);
  if (!media.length) return null;
  return { ...post, media };
}

/** In-flight / resolved photo catalog. */
let photosPromise: Promise<PhotoPost[]> | null = null;
let photosCache: PhotoPost[] | null = null;

export function getCachedPhotos(): PhotoPost[] | null {
  return photosCache;
}

export function preloadPhotos(): Promise<PhotoPost[]> {
  if (!photosPromise) {
    photosPromise = fetch(`${API_BASE}/data/photo`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to fetch photos');
        const list = (await res.json()) as PhotoPost[];
        const filtered = (Array.isArray(list) ? list : [])
          .map((p) => filterPublicPhotoPost(p))
          .filter((p): p is PhotoPost => Boolean(p))
          .sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
          );
        photosCache = filtered;
        return filtered;
      })
      .catch((err) => {
        photosPromise = null;
        throw err;
      });
  }
  return photosPromise;
}

export async function fetchPhotos(): Promise<PhotoPost[]> {
  return preloadPhotos();
}

export async function fetchPhoto(id: string): Promise<PhotoPost> {
  const res = await fetch(`${API_BASE}/data/photo/${id}`);
  if (!res.ok) throw new Error('Failed to fetch photo');
  const post = filterPublicPhotoPost(await res.json());
  if (!post) throw new Error('Failed to fetch photo');
  return post;
}

/** In-flight / resolved cache so portrait + Infos share one network round-trip. */
let infoPromise: Promise<SiteInfo> | null = null;
let infoCache: SiteInfo | null = null;

/** Sync peek for skipping skeletons when preload already finished. */
export function getCachedInfo(): SiteInfo | null {
  return infoCache;
}

export function preloadInfo(): Promise<SiteInfo> {
  if (!infoPromise) {
    infoPromise = fetch(`${API_BASE}/data/info`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to fetch info');
        const data = (await res.json()) as SiteInfo;
        infoCache = data;
        return data;
      })
      .catch((err) => {
        infoPromise = null;
        throw err;
      });
  }
  return infoPromise;
}

export async function fetchInfo(): Promise<SiteInfo> {
  return preloadInfo();
}

/** Split TipTap HTML / legacy plain bio into renderable paragraphs or HTML block. */
export function parseBioContent(bio: string): { bioHtml: string | null; paragraphs: string[] } {
  const raw = bio || '';
  if (/<\s*[a-z]/i.test(raw)) {
    const singleP = raw.match(/^\s*<p(?:\s[^>]*)?>([\s\S]*)<\/p>\s*$/i);
    if (singleP && !/<(?:p|div|ul|ol|table|h[1-6]|br)\b/i.test(singleP[1])) {
      const parts = singleP[1]
        .replace(/&#10;/g, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .split(/\n\n+/)
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length > 1) return { bioHtml: null, paragraphs: parts };
    }
    return { bioHtml: raw, paragraphs: [] };
  }
  return { bioHtml: null, paragraphs: raw.split(/\n\n+/).filter(Boolean) };
}

export async function fetchReleasesSlim(): Promise<SlimRelease[]> {
  const res = await fetch(`${API_BASE}/data/music/releases`);
  if (!res.ok) throw new Error('Failed to fetch releases');
  return res.json();
}

export async function submitContact(body: Record<string, string>): Promise<void> {
  const res = await fetch('/api/info/contact', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to send');
}

export function isPlayable(track: Track): boolean {
  return track.duration !== 0 && Boolean(track.streamUrl?.trim());
}

/** Decode an image URL into browser cache before a view transition paints it. */
export function preloadImageUrl(
  src: string,
  timeoutMs = 2500,
): Promise<void> {
  if (!src) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(done, timeoutMs);
    img.onload = done;
    img.onerror = done;
    img.src = src;
    if (img.complete && img.naturalWidth > 0) done();
  });
}

function preloadVideoUrl(src: string, timeoutMs = 2500): Promise<void> {
  if (!src) return Promise.resolve();
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = window.setTimeout(done, timeoutMs);
    video.addEventListener('loadeddata', done, { once: true });
    video.addEventListener('error', done, { once: true });
    video.preload = 'auto';
    video.src = src;
  });
}

export function isMotionArtwork(path?: string | null): boolean {
  return Boolean(path && /\.(mp4|webm|mov|gif|webp)(\?|$)/i.test(path));
}

export function isVideoMotionArtwork(path?: string | null): boolean {
  return Boolean(path && /\.(mp4|webm|mov)(\?|$)/i.test(path));
}

export function resolveArtworkMotion(path?: string | null): string {
  if (!path) return '';
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return mediaUrl(normalized);
}

/** Warm release artwork before catalog → detail view transition. */
export function preloadReleaseArtwork(
  release: Pick<Release, 'artworkUrl' | 'artworkMotionUrl'>,
): Promise<void> {
  const motion = resolveArtworkMotion(release.artworkMotionUrl);
  if (isVideoMotionArtwork(motion)) {
    return Promise.all([
      preloadVideoUrl(motion),
      preloadImageUrl(artworkUrl(release.artworkUrl)),
    ]).then(() => undefined);
  }
  if (motion && /\.gif(\?|$)/i.test(motion)) {
    return preloadImageUrl(motion);
  }
  return preloadImageUrl(artworkUrl(release.artworkUrl));
}

/** Warm first visible photo/video (or poster) before catalog → detail VT. */
export function preloadPhotoCover(post: PhotoPost): Promise<void> {
  const media = (post.media || []).find((m) => m && m.hidden !== true);
  if (!media) return Promise.resolve();
  const isVideo =
    media.mediaFormat === 'Video' ||
    /\.(mp4|webm)(\?|$)/i.test(media.mediaUrl);
  if (isVideo && media.posterUrl) {
    return preloadImageUrl(mediaUrl(media.posterUrl));
  }
  if (isVideo) return preloadVideoUrl(mediaUrl(media.mediaUrl));
  return preloadImageUrl(mediaUrl(media.mediaUrl));
}

export function preloadMediaItems(media: { mediaUrl: string }[]): void {
  media.forEach((item) => {
    const src = mediaUrl(item.mediaUrl);
    if (/\.(mp4|webm)(\?|$)/i.test(item.mediaUrl)) {
      void preloadVideoUrl(src);
    } else {
      void preloadImageUrl(src);
    }
  });
}
